import { useCallback, useEffect } from 'react';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { CustomerLifecycleManagementOrg } from '@wetags/customer-lifecycle-management';
import {
  PlatformLocationFeatureServiceName,
  type PlatformLocationFeatureServiceQueries,
} from '@frontend/api-platform-location-feature';
import { useSchemaQueryUpdaters } from '@frontend/react-query-helpers';
import { GetWebsocketEventPayload, useWebsocketEventPublisher } from '@frontend/websocket';

export const useAICallCreditWebsocket = (): void => {
  const [isLimitEnforcementEnabled] = CustomerLifecycleManagementOrg.useWetag('air-credit-enforcement');

  const { updateQuery } = useSchemaQueryUpdaters<PlatformLocationFeatureServiceQueries>(
    PlatformLocationFeatureServiceName
  );
  const publisher = useWebsocketEventPublisher();

  const handleCreditChangeEvent = useCallback(
    (event: GetWebsocketEventPayload<NotificationType.VOICE_AI_CREDIT_CHANGE>) => {
      const { locationId, credits } = event.params;

      if (!locationId || !isLimitEnforcementEnabled) {
        return;
      }

      updateQuery({
        endpointName: 'GetLocationFeatureCreditDetails',
        queryFilters: {
          predicate(query) {
            const requestLocationIds = query.queryKey[2]?.locationIds;
            return !!requestLocationIds && Array.isArray(requestLocationIds) && requestLocationIds.includes(locationId);
          },
        },
        updater: (data) => {
          return {
            locationCreditDetails: {
              ...data.locationCreditDetails,
              [locationId]: {
                ...data.locationCreditDetails?.[locationId],
                credits: {
                  ...data.locationCreditDetails?.[locationId]?.credits,
                  ...credits,
                },
              },
            },
          };
        },
      });
    },
    [updateQuery, isLimitEnforcementEnabled]
  );

  useEffect(() => {
    publisher.on(NotificationType.VOICE_AI_CREDIT_CHANGE, handleCreditChangeEvent);
    return () => {
      publisher.off(NotificationType.VOICE_AI_CREDIT_CHANGE, handleCreditChangeEvent);
    };
  }, [publisher, handleCreditChangeEvent]);
};
